import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { AppRoutes } from "@/constants/routes";
import { Card, CheckboxCard, Field, Flex, Grid, GridItem, Text } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  CounterDeliveryHarvest,
  counterDeliverySchema,
} from "../schema/counter-delivery.schema";
import { isEmpty } from "@/utils/query-string";
import { GranoEntity } from "../../sell-orders/schema/sell-order.schema";
import { getListadoGranos } from "@/services/aux-entities.service";
import { getCounterDeliveryHarvests } from "@/services/counter-delivery.service";
import { TextArea } from "@/components/text-area/TextArea";
import { AppColors } from "@/styles/colors";
import { AsyncAutocompleteSelect, Option } from "@/components/select/AsyncAutocompleteSelect";
import { createCounterDeliveryHandler, getOptionsHandler, loadOptionsHandler } from "../schema/handler-functions";
import { ComboboxComponent } from "@/components/select/Combobox";
import { SelectComponent } from "@/components/select/Select";
import { InputField } from "@/components/input-field/InputField";
import { numbersWithCommaDecimal } from "@/utils/reg-expressions";
import { InputCurrency } from "@/components/input-currency/InputCurrency";
import { useAuth } from "@/hooks/useAuth";
import { useRoles } from "@/hooks/useRoles";

export const CounterDeliveryCreationPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);
  useEffect(() => {
    setIsActive(true);
  }, []);

  const { control, formState: { errors }, setValue, register } = useForm({
    resolver: zodResolver(counterDeliverySchema),
  });

  const [loadingCreateCounterDelivery, setLoadingCreateCounterDelivery] =
    useState<boolean>(false);
  const [counterDeliveryHarvest, setCounterDeliveryHarvest] = useState<
    CounterDeliveryHarvest[] | null
  >([]);
  const [loadingCounterDeliveryHarvest, setLoadingCounterDeliveryHarvest] =
    useState<boolean>(false);

    const [loadingPersons, setLoadingPersons] = useState<boolean>(false)

  const [loadingGrains, setLoadingGrains] = useState<boolean>(false);

  const [grains, setGrains] = useState<GranoEntity[] | null>([]);

  const navigate = useNavigate();

  const handleCancel = () => {
    navigate(AppRoutes.GRAIN_DELIVERY_PAYMENTS);
  };

  const fetchGrains = async () => {
    setLoadingGrains(true);
    const response = await getListadoGranos();
    setGrains(response);
    setLoadingGrains(false);
  };

  const fetchCounterDeliveryHarvests = async () => {
    setLoadingCounterDeliveryHarvest(true);
    const response = await getCounterDeliveryHarvests();
    setCounterDeliveryHarvest(response);
    setLoadingCounterDeliveryHarvest(false);
  };
  useEffect(() => {
    if (isActive) {
      fetchCounterDeliveryHarvests();
      fetchGrains();
    }
  }, [isActive]);

  const { user } = useAuth()

  const { fetchPermissions } = useRoles()

  const handleCreateCounterDelivery = async () => {
    createCounterDeliveryHandler({
      fetchPermissions,
      navigate,
      setLoadingCreateCounterDelivery,
      user: user?.name ?? "",
      watchedValues
    })
  };

  const watchedValues = useWatch({ control });

  const hasNoFilledValues = useMemo(() => {
    const { account, grain, harvest, ton } = watchedValues || {};

    return (
      isEmpty(account) || isEmpty(grain) || isEmpty(harvest) || isEmpty(ton)
    );
  }, [watchedValues]);

  const handleTonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value || "";
    // Normalize dots to commas
    val = val.replaceAll('.', ',');
    // Keep only digits and comma
    val = val.replaceAll(/[^0-9,]/g, '');
    // Ensure single comma and max 3 decimals
    const parts = val.split(',');
    if (parts.length > 1) {
      const intPart = parts[0];
      const decPart = (parts.slice(1).join('')).slice(0, 3);
      val = decPart.length > 0 ? `${intPart},${decPart}` : `${intPart},`;
    } else {
      val = parts[0];
    }
    // Prevent leading comma (",123") -> "0,123"
    if (val.startsWith(',')) {
      val = `0${val}`;
    }
    e.target.value = val;
  }

    const getOptions = async (
      inputValue: string,
    ): Promise<
      {
        label: string;
        value: string;
        tipoCuenta: string;
        idTipoCuenta: number;
      }[]
    > => {
      return getOptionsHandler({
        inputValue,
      });
    };
  
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  
    const loadOptions = (inputValue: string): Promise<Option[]> => {
      return loadOptionsHandler({
        getOptions,
        inputValue,
        setLoadingPersons,
        timeoutRef,
      });
    };
  
  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Agregar venta de granos a entregar"
        addButtonLabel="Agregar"
        cancelButtonLabel="Cancelar"
        handleCancel={handleCancel}
        handleAdd={handleCreateCounterDelivery}
        disableAdd={hasNoFilledValues}
        loading={loadingCreateCounterDelivery}
      />

            <Card.Root maxW="full" marginTop="30px" borderRadius="8px">
              <Card.Body
                paddingX="48px"
                paddingY="30px"
                borderRadius="8px"
                border={`1px solid ${AppColors.WHITE_BORDER_1}`}
              >
                <Grid templateColumns="repeat(4, 3fr)">
                  <GridItem paddingX="16px" paddingY="8px">
                    <Field.Root invalid={!!errors?.account?.message} gap="12px">
                      <Field.Label>
                        {" "}
                        <Text fontSize="16px" fontWeight={600}>
                          Cuenta
                        </Text>
                      </Field.Label>
      
                      <AsyncAutocompleteSelect
                        control={control}
                        loadOptions={loadOptions}
                        name="account"
                        placeholder="Buscar..."
                        isLoading={loadingPersons}
                        disabled={loadingCreateCounterDelivery}
                      />
      
                      <Field.ErrorText>
                        {String(errors?.account?.message)}
                      </Field.ErrorText>
                    </Field.Root>
                  </GridItem>
                 
                  <GridItem paddingY="8px" paddingX="16px">
                    <Field.Root invalid={!!errors?.grain?.message}>
                      <Field.Label>
                        <Text fontSize="16px" fontWeight={600}>
                          Grano
                        </Text>
                      </Field.Label>
      
                      <ComboboxComponent
                        key={grains?.length ? `grain-loaded` : "grain-empty"}
                        items={
                          grains?.map((el) => ({
                            label: el?.nombre,
                            value: el?.id,
                          })) || []
                        }
                        menuMaxHeight="250px"
                        name="grain"
                        control={control}
                        clearable
                        placeholder="Seleccionar grano"
                        loading={loadingCreateCounterDelivery || loadingGrains}
                        disabled={loadingCreateCounterDelivery || loadingGrains}
                      />
      
                      <Field.ErrorText>
                        {String(errors?.grain?.message)}
                      </Field.ErrorText>
                    </Field.Root>
                  </GridItem>
      
                  <GridItem paddingY="8px" paddingX="16px">
                    <Field.Root invalid={!!errors?.harvest?.message}>
                      <Field.Label>
                        <Text fontSize="16px" fontWeight={600}>
                          Cosecha
                        </Text>
                      </Field.Label>
                      <SelectComponent
                        fullWidth
                        items={
                          counterDeliveryHarvest?.map((el) => ({
                            label: `${el.cosecha.slice(0, 2)}-${el.cosecha.slice(2)}`,
                            value: el.cosecha,
                          })) || []
                        }
                        menuMaxHeight="250px"
                        name="harvest"
                        control={control}
                        closeOnSelect
                        clearable
                        size="xs"
                        minHeight="40px"
                        placeholder="Seleccionar cosecha"
                        loading={loadingCounterDeliveryHarvest}
                        disabled={
                          loadingCreateCounterDelivery ||
                          loadingCounterDeliveryHarvest ||
                          Boolean(!counterDeliveryHarvest?.length)
                        }
                      />
                      <Field.ErrorText>
                        {String(errors?.harvest?.message)}
                      </Field.ErrorText>
                    </Field.Root>
                  </GridItem>
      
                  <GridItem paddingY="8px" paddingX="16px">
                    <InputField
                      inputName="ton"
                      labelText="Toneladas"
                      control={control}
                      allowedPattern={numbersWithCommaDecimal}
                      type="text"
                      onChange={handleTonChange}
                      disabled={loadingCreateCounterDelivery}
                      placeholder="Ingrese toneladas"
                    />
                  </GridItem>
      
                  <GridItem paddingX="16px" paddingY="2px">
                    <Field.Root invalid={!!errors?.price?.message} gap="12px">
                      <Field.Label>
                        <Text fontSize="16px" fontWeight={600}>
                          Precio
                        </Text>
                      </Field.Label>
                      <InputCurrency
                        control={control}
                        name="price"
                        height="40px"
                        maxLength={20}
                      />
      
                      <Field.ErrorText>
                        {String(errors?.price?.message)}
                      </Field.ErrorText>
                    </Field.Root>
                  </GridItem>
      
                  <GridItem paddingX="16px" paddingY="30px">
                    <CheckboxCard.Root maxW="240px">
                      <CheckboxCard.HiddenInput
                        value={watchedValues.isExchange ? "on" : "off"}
                        onChange={(e) => setValue("isExchange", e.target.checked)}
                      />
                      <CheckboxCard.Control paddingX="16px" paddingY="12px">
                        <CheckboxCard.Label>
                          {" "}
                          <Text fontSize="16px" fontWeight={600}>
                            Es canje
                          </Text>
                        </CheckboxCard.Label>
                        <CheckboxCard.Indicator />
                      </CheckboxCard.Control>
                    </CheckboxCard.Root>
                  </GridItem>
                </Grid>
              </Card.Body>
            </Card.Root>
            <Flex flexDir="column" gap="12px">
              <Text fontWeight={700} fontSize="20px">
                Detalle
              </Text>
              <TextArea
                errors={errors}
                inputName="order_detail"
                maxLength={50}
                register={register}
                placeholder="Ingresar una descripción de la venta de granos a entregar"
                helperText="Máximo 50 caracteres"
              />
            </Flex>
    </Flex>
  );
};
